import { createSelector } from 'reselect';
import { createFeatureSelector } from 'features/create-feature-selector';
import { getVariantsReviewProducts } from 'features/related-products/selectors/get-variants-review-products';
import { GROUP_OF_VARIANTS, SINGLE_PRODUCT } from 'common/constants';
import { getReviewsPhotosAllData } from 'features/reviews-photos/selectors/reviews-photos';
import { enrichGallery } from '../utils/enrich-gallery';
import {
  enrichReviewsWithVariants,
  enrichReviewsWithVariantsPagination,
} from '../utils/enrich-reviews-with-variants';

const reviewsFeatureSelector = createFeatureSelector('reviews');
export const getReviewsSorting = createSelector(reviewsFeatureSelector, (f) => f.reviewsSorting);
export const getReviewsByScoreFilters = createSelector(
  reviewsFeatureSelector,
  (f) => f.reviewsFilters.byScore,
);

export const getReviewsRatingTotal = createSelector(reviewsFeatureSelector, (f) => f.reviews?.rating.total);

export const getReviewsGroupRatingTotal = createSelector(reviewsFeatureSelector, (f) => {
  const reviewsGroup = f?.reviewsGroup ?? GROUP_OF_VARIANTS;
  const isGroupRatings = f.isGroupRatings;

  if (reviewsGroup === GROUP_OF_VARIANTS && isGroupRatings) {
    return {
      scoreRating: f.reviews?.rating?.total?.groupScore,
      countReview: f.reviews?.rating?.total?.groupReviewCount,
    };
  } else {
    return {
      scoreRating: f.reviews?.rating?.total?.score,
      countReview: f.reviews?.rating?.total?.count,
    };
  }
});

export const getReviewsGroupRatingTotalByGroupId = createSelector(reviewsFeatureSelector, (f) => {
  const groupId = f?.reviews?.groupId;
  const isGroupRatings = f.isGroupRatings;

  if (groupId === null || !isGroupRatings) {
    return {
      scoreRating: f.reviews?.rating?.total?.score,
      countReview: f.reviews?.rating?.total?.count,
    };
  }

  return {
    scoreRating: f.reviews?.rating?.total?.groupScore,
    countReview: f.reviews?.rating?.total?.groupReviewCount,
  };
});

export const checkIfReviewsAreLoaded = createSelector(
  reviewsFeatureSelector,
  (f) => !!f.reviews?.reviewsCards?.length && f.reviewsError === null,
);

export const getReviewsProductId = createSelector(reviewsFeatureSelector, (f) => f.reviews?.productId);
export const getReviewsPageMeta = createSelector(reviewsFeatureSelector, (f) => f.reviews?.pageMeta ?? null);

export const getReviewsCards = createSelector(reviewsFeatureSelector, (f) => f.reviews?.reviewsCards ?? []);

export const getReviewsCardsAndVariants = createSelector(
  [getReviewsCards, getVariantsReviewProducts],
  (reviewsCards, variantsModel) => {
    return enrichReviewsWithVariants(reviewsCards, variantsModel);
  },
);

export const getReviewsPerPage = createSelector(
  reviewsFeatureSelector,
  (f) => f.reviews?.pageMeta.reviewsPerPage,
);
const getReviewsPagination = createSelector(reviewsFeatureSelector, (f) => f.pagination);

export const getReviewsPaginationWithVariants = createSelector(
  [getReviewsPagination, getVariantsReviewProducts],
  (paginationTail, variantsModel) => {
    return enrichReviewsWithVariantsPagination(paginationTail, variantsModel);
  },
);

export const getAllPhotosFromReviews = createSelector(
  [reviewsFeatureSelector, getReviewsCards, getVariantsReviewProducts, getReviewsPhotosAllData],
  (f, reviewCards, variantsModel, reviewsPhotosData) => {
    const galleryPhotos = f.reviews?.gallery ?? [];
    const paginationGalleryPhotos = reviewsPhotosData ?? [];

    const merged = [...galleryPhotos, ...paginationGalleryPhotos];
    const seen = new Set<string>();
    const deduplicated = merged.filter((photo) => {
      if (seen.has(photo.urlLarge)) return false;
      seen.add(photo.urlLarge);
      return true;
    });
    return enrichGallery(deduplicated, reviewCards, variantsModel);
  },
);

export const getPaginationError = createSelector(
  reviewsFeatureSelector,
  (r) => r?.paginationLastPageError ?? null,
);

export const isReviewsPaginationPageLoading = createSelector(
  reviewsFeatureSelector,
  (f) => f.paginationPageIsLoading,
);
export const areReviewsLoading = createSelector(reviewsFeatureSelector, (f) => f?.reviewsLoading ?? false);

export const getReviewsAllPhotos = createSelector(reviewsFeatureSelector, (f) => {
  return f.reviews?.allPhotos ?? [];
});

const getReviewsPhotosModal = createSelector(reviewsFeatureSelector, (f) => f?.photosModal ?? null);
export const isReviewsPhotosModalShowed = createSelector(
  getReviewsPhotosModal,
  (pm) => pm?.isShowed ?? false,
);
export const getReviewsPhotosModalCurrentShowed = createSelector(
  getReviewsPhotosModal,
  (pm) => pm?.currentShowed ?? null,
);

const getReviewsPhotosModalSource = createSelector(getReviewsPhotosModal, (pm) => pm?.source ?? 'review');

export const isReviewsPhotosModalOpenedFromGallery = createSelector(
  getReviewsPhotosModalSource,
  (source) => source === 'gallery',
);

export const getReviewsOpinions = createSelector(reviewsFeatureSelector, (f) => f?.opinions.records ?? []);
export const getLikeDislikeVoterId = createSelector(reviewsFeatureSelector, (f) => f.opinions.voterId);

export const photosUploadHasError = createSelector(reviewsFeatureSelector, (f) =>
  f.selectedPhotos.some(({ processingIsFailed }) => {
    // eslint-disable-next-line no-console
    console.log('processingIsFailed', processingIsFailed);
    return processingIsFailed;
  }),
);

export const getReviewGroupSorting = createSelector(reviewsFeatureSelector, (f) => {
  const groupId = f?.reviews?.groupId;

  if (groupId === null) {
    return SINGLE_PRODUCT;
  }

  return f?.reviewsGroup ?? GROUP_OF_VARIANTS;
});

export const getGroupId = createSelector(reviewsFeatureSelector, (f) => f.reviews?.groupId ?? null);

export const getIsGroupRatings = createSelector(reviewsFeatureSelector, (f) => f?.isGroupRatings);
